import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchLivingUnits, postLivingUnit, SiteEntryProps, TLivingUnit } from '@ixicare/resources';
import { sortBy } from 'lodash';
import React from 'react';

interface UseLivingUnitSelectProps {
  site: SiteEntryProps;
  initialValue?: string;
}

export const useLivingUnitSelect = ({ site, initialValue }: UseLivingUnitSelectProps) => {
  const queryClient = useQueryClient();
  const livingUnits = useQuery({
    queryKey: ['livingUnits', site.uuid],
    queryFn: () => fetchLivingUnits({ siteId: site.uuid }),
  });

  const options = React.useMemo(
    () =>
      sortBy(
        livingUnits.data?.map((x) => ({
          label: x.name,
          value: x.id,
        })),
        'label'
      ) ?? [],
    [livingUnits.data]
  );

  const onChange = (id, name) => {
    handleLivingUnitChange(id);
    handleLivingUnitNameChange(name);
  };

  const createLuMutation = useMutation({ mutationFn: postLivingUnit });

  async function handleCreateOption(luName: string) {
    const resp = await createLuMutation.mutateAsync({ siteId: site.uuid, name: luName });
    queryClient.setQueryData<TLivingUnit[]>(['livingUnits', site.uuid], (data) => (data ?? [])?.concat(resp));
    livingUnits.refetch();
    onChange(resp.id, resp.name);
  }

  const [livingUnitId, handleLivingUnitChange] = React.useState<string | undefined>(initialValue);
  const [livingUnitName, handleLivingUnitNameChange] = React.useState<string>();

  return {
    options,
    livingUnits,
    livingUnitId,
    livingUnitName,
    createLuMutation,
    handleCreateOption,
    onChange,
  };
};
